// Visual state of a V2 locker door tile, derived from its active SST V2
// reservation. Extracted from OsLockerDoorV2 so the threshold behaviour is
// unit-testable — the "about to expire" window is tenant-configurable and
// regressed once already (DEV-212: V2 hard-coded 15 min while V1 read the
// tenant's `configAboutToExpirationReminderDelayMinutes`, default 1440).

import { parseSst2Utc } from "./sst2DateUtils"
import { isSst2ReservationPickedUp } from "./sst2Status"

/** Fallback when the tenant's Configuration thing hasn't loaded (matches V1). */
export const DEFAULT_EXPIRATION_WARNING_MINUTES = 15

export type Sst2DoorStateReservation = {
  status: string
  expiration: string | null
}

export type Sst2DoorState = {
  statusColor: string
  statusTextColor: string
  hasExpired: boolean
  showExpirationWarning: boolean
  isDelivered: boolean
  isDraft: boolean
  /** The parcel is out of the locker — CLOSE / CLOSED. See `sst2Status.ts`. */
  isCompleted: boolean
  /** Formatted remaining (or elapsed, when expired) time — "" when unknown. */
  expiresIn: string
}

const AVAILABLE_STATE: Sst2DoorState = {
  statusColor: "#43A047",
  statusTextColor: "whitesmoke",
  hasExpired: false,
  showExpirationWarning: false,
  isDelivered: false,
  isDraft: false,
  isCompleted: false,
  expiresIn: "",
}

/**
 * Compute the formatted "time remaining" or "time elapsed" string from a diff
 * in seconds. Matches V1 format: "2j 3h", "5h 12m", "8m".
 */
export const formatTimeDiff = (absDiffSeconds: number): string => {
  const days = Math.floor(absDiffSeconds / 86400)
  const hours = Math.floor((absDiffSeconds % 86400) / 3600)
  const minutes = Math.floor((absDiffSeconds % 3600) / 60)
  if (days > 0) return `${days}j ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

/**
 * Derive the door tile's visual state. Mirrors V1's `useReservation` colour
 * logic:
 *   Picked up        → green + whitesmoke   (FIRST — see below)
 *   Draft            → lightblue + primary text
 *   Expired          → red + whitesmoke
 *   About to expire  → orange + whitesmoke
 *   Active (CONFIRMED / DELIVERED) → white + primary text
 *   No reservation   → green + whitesmoke
 *
 * LOCKER-402: the picked-up branch is first, and it also suppresses `hasExpired`.
 *
 * V1's `useReservation` has `isCompleted` first in its colour ternary; this port
 * kept `hasExpired`, `showExpirationWarning`, `isDraft` and `isDelivered` and
 * dropped `isCompleted` alone. So after an SST v1→v2 migration, a parcel the
 * customer had already collected rendered red with an expiration code next to a
 * green (available) door — because the row's expiration deadline still passed.
 *
 * Suppressing `hasExpired` rather than only re-ordering the colours is the point:
 * the flag is what the tile's `uf-door-expired` class, its "since" label and the
 * drawer's expiration-code box all read. An expiration deadline that passes after
 * the parcel left the locker is not an expiry — there is nothing behind the door
 * to recover.
 *
 * **`useReservation` now applies the identical guard**, so V1 and V2 derive the
 * same flags from the same row rather than agreeing by coincidence — which is the
 * coincidence that broke when this file was ported. `hooks/useReservation.parity.test.tsx`
 * drives both implementations over one case table and fails on any divergence;
 * add a case there, not here, when the taxonomy changes.
 *
 * `CANCEL` / `CANCELLED` are NOT in the picked-up set, so they keep expiring
 * normally (LOCKER-391: on the partner side those are active and the parcel is
 * still inside).
 *
 * @param warningMinutes tenant-configured "about to expire" window, in minutes.
 */
export function computeSst2DoorState({
  reservation,
  now,
  warningMinutes = DEFAULT_EXPIRATION_WARNING_MINUTES,
}: {
  reservation?: Sst2DoorStateReservation | null
  now: Date
  warningMinutes?: number
}): Sst2DoorState {
  if (!reservation) return AVAILABLE_STATE

  const status = reservation.status
  const isDraft = status === "DRAFT"
  const isDelivered = status === "DELIVERED"
  const isExpiredStatus = status === "EXPIRED"
  const isCompleted = isSst2ReservationPickedUp(reservation)

  // SST V2 emits `expiration` as naive UTC ("YYYY-MM-DD HH:mm:ss", no T/Z).
  // new Date(str) would parse non-ISO formats as browser-local, drifting by
  // the operator's TZ offset (+4h in EDT).
  const expirationDate = parseSst2Utc(reservation.expiration)
  const diffInSeconds = expirationDate
    ? (expirationDate.getTime() - now.getTime()) / 1000
    : Infinity

  const hasExpired = !isCompleted && (isExpiredStatus || diffInSeconds <= 0)
  const showExpirationWarning =
    !isCompleted && !hasExpired && diffInSeconds < warningMinutes * 60

  const expiresIn = expirationDate ? formatTimeDiff(Math.abs(diffInSeconds)) : ""

  let statusColor: string
  let statusTextColor: string

  if (isCompleted) {
    // Same green as an available door, and V1 puts this first for the same
    // reason: the parcel is gone, so nothing else about the row can matter.
    statusColor = "#43A047"
    statusTextColor = "whitesmoke"
  } else if (isDraft) {
    statusColor = "lightblue"
    statusTextColor = "primary.main"
  } else if (hasExpired) {
    statusColor = "#FF6B6B"
    statusTextColor = "whitesmoke"
  } else if (showExpirationWarning) {
    statusColor = "orange"
    statusTextColor = "whitesmoke"
  } else {
    // Active reservation (CONFIRMED or DELIVERED), not expired
    statusColor = "white"
    statusTextColor = "primary.main"
  }

  return {
    statusColor,
    statusTextColor,
    hasExpired,
    showExpirationWarning,
    isDelivered,
    isDraft,
    isCompleted,
    expiresIn,
  }
}
